(function () {
  const SECTION_CLASS = "shopify-section--pdp-metafield-gallery";
  const MOBILE_MQ = window.matchMedia("(max-width: 767px)");

  function getSectionRoot(node) {
    if (!node) return null;
    if (node.classList?.contains(SECTION_CLASS)) return node;
    return node.querySelector?.(`.${SECTION_CLASS}`) || node.closest?.(`.${SECTION_CLASS}`);
  }

  function destroyMobileCarousel(sectionRoot) {
    const mobile = sectionRoot?.querySelector("[data-pdp-gallery-mobile]");
    if (!mobile) return;

    if (mobile.__pdpGalleryScrollRaf) {
      window.cancelAnimationFrame(mobile.__pdpGalleryScrollRaf);
      mobile.__pdpGalleryScrollRaf = 0;
    }

    if (mobile.__pdpGalleryAbort) {
      mobile.__pdpGalleryAbort.abort();
      mobile.__pdpGalleryAbort = null;
    }
  }

  function getSlides(track) {
    return Array.from(track.querySelectorAll("[data-pdp-gallery-slide]"));
  }

  function getNearestSlideIndex(track, slides) {
    if (!slides.length) return 0;

    const trackCenter = track.scrollLeft + track.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  function setActiveSlide(slides, activeIndex) {
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.classList.toggle("is-active", isActive);
      slide.classList.toggle("is-adjacent", Math.abs(index - activeIndex) === 1);
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  }

  function scrollToSlide(track, slides, index, behavior) {
    const slide = slides[index];
    if (!slide) return;

    const targetLeft = slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
    track.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: behavior || "smooth",
    });
  }

  function releaseNavFocus(button) {
    if (button && typeof button.blur === "function") {
      button.blur();
    }
  }

  function bindTrackPointerDrag(track, slides, signal) {
    let activePointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let hasDragged = false;

    track.addEventListener(
      "dragstart",
      (event) => {
        event.preventDefault();
      },
      { capture: true, signal }
    );

    const endDrag = (event) => {
      if (activePointerId === null || event.pointerId !== activePointerId) return;

      track.classList.remove("is-dragging");

      try {
        track.releasePointerCapture(activePointerId);
      } catch (_error) {
        /* pointer already released */
      }

      activePointerId = null;

      if (hasDragged) {
        scrollToSlide(track, slides, getNearestSlideIndex(track, slides), "smooth");
      }
    };

    track.addEventListener(
      "pointerdown",
      (event) => {
        if (event.pointerType !== "mouse" || event.button !== 0) return;
        if (event.target.closest("button, a, input, label")) return;
        if (track.scrollWidth <= track.clientWidth) return;

        event.preventDefault();

        activePointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = track.scrollLeft;
        hasDragged = false;
        track.classList.add("is-dragging");
        track.setPointerCapture(activePointerId);
      },
      { signal }
    );

    track.addEventListener(
      "pointermove",
      (event) => {
        if (activePointerId === null || event.pointerId !== activePointerId) return;

        const deltaX = event.clientX - startX;
        if (Math.abs(deltaX) > 4) hasDragged = true;
        if (!hasDragged) return;

        event.preventDefault();
        track.scrollLeft = startScrollLeft - deltaX;
      },
      { signal }
    );

    track.addEventListener("pointerup", endDrag, { signal });
    track.addEventListener("pointercancel", endDrag, { signal });
  }

  function initMobileCarousel(sectionRoot) {
    if (!sectionRoot || !MOBILE_MQ.matches) return;

    const track = sectionRoot.querySelector(".pdp-metafield-gallery__track");
    if (!track) return;

    const mobile = track.closest("[data-pdp-gallery-mobile]");
    if (!mobile) return;

    const slides = getSlides(track);
    if (!slides.length) return;

    destroyMobileCarousel(sectionRoot);

    const nav = sectionRoot.querySelector("[data-pdp-gallery-nav]");
    const prevBtn = sectionRoot.querySelector(".pdp-metafield-gallery__nav-btn--prev");
    const nextBtn = sectionRoot.querySelector(".pdp-metafield-gallery__nav-btn--next");
    const slideCount = slides.length;

    if (nav) {
      nav.hidden = slideCount <= 1;
    }

    const abort = new AbortController();
    mobile.__pdpGalleryAbort = abort;
    const signal = abort.signal;

    let activeIndex = 0;

    const syncFromScroll = () => {
      activeIndex = getNearestSlideIndex(track, slides);
      setActiveSlide(slides, activeIndex);
    };

    track.addEventListener(
      "scroll",
      () => {
        if (mobile.__pdpGalleryScrollRaf) return;
        mobile.__pdpGalleryScrollRaf = window.requestAnimationFrame(() => {
          mobile.__pdpGalleryScrollRaf = 0;
          syncFromScroll();
        });
      },
      { passive: true, signal }
    );

    bindTrackPointerDrag(track, slides, signal);

    if (slideCount > 1) {
      if (prevBtn) {
        prevBtn.addEventListener(
          "click",
          () => {
            const nextIndex = (activeIndex - 1 + slideCount) % slideCount;
            scrollToSlide(track, slides, nextIndex, "smooth");
            releaseNavFocus(prevBtn);
          },
          { signal }
        );
      }

      if (nextBtn) {
        nextBtn.addEventListener(
          "click",
          () => {
            const nextIndex = (activeIndex + 1) % slideCount;
            scrollToSlide(track, slides, nextIndex, "smooth");
            releaseNavFocus(nextBtn);
          },
          { signal }
        );
      }
    }

    requestAnimationFrame(() => {
      scrollToSlide(track, slides, 0, "auto");
      syncFromScroll();
    });
  }

  function refreshSection(sectionRoot) {
    if (!sectionRoot) return;
    destroyMobileCarousel(sectionRoot);
    if (MOBILE_MQ.matches) initMobileCarousel(sectionRoot);
  }

  function bootAll() {
    document.querySelectorAll(`.${SECTION_CLASS}`).forEach(refreshSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootAll, { once: true });
  } else {
    bootAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    refreshSection(getSectionRoot(event.target));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyMobileCarousel(getSectionRoot(event.target));
  });

  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(bootAll, 200);
  });

  if (typeof MOBILE_MQ.addEventListener === "function") {
    MOBILE_MQ.addEventListener("change", bootAll);
  } else if (typeof MOBILE_MQ.addListener === "function") {
    MOBILE_MQ.addListener(bootAll);
  }
})();
